import AccountListItem from "@components/AccountListItem/AccountListItem";
import Checkbox from "@components/Checkbox/Checkbox";
import BottomSheet from "@components/BottomSheet/BottomSheet";
import BottomSheetHeader from "@components/BottomSheetHeader/BottomSheetHeader";
import Dropdown from "@components/Dropdown/Dropdown";
import Header from "@components/Header/Header"
import PrimaryButton from "@components/PrimaryButton/PrimaryButton";
import SearchBar from "@components/SearchBar/SearchBar";
import TextInput from "@components/TextInput/TextInput";
import { INTER } from "@styles/fonts";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router"
import React from "react";
import { useTranslation } from "react-i18next";
import {
    Body,
    ButtonContainer,
    Container,
    Content,
    Divider,
    DropdownContainer,
    EmptyStateContainer,
    FormContainer,
    HeaderContainer,
    HeaderTitle,
    HistoryLink,
    InputContainer,
    FooterText,
    HintContainer,
    HintInner,
    HintText,
    SaveToFavoritesContainer,
} from "./BankTransferPayment.styles";
import { useNumericKeyboard } from "@hooks/useNumericKeyboard";
import { filterBySearchQuery, toCurrencyFormat } from "@lib/utils";
import { RupiahInput } from "@components/RupiahInput/RupiahInput";
import useBankTransferPaymentFee from "@hooks/useBankTransferPaymentFee";
import useManagePaymentBank from "@hooks/useManagePaymentBank";
import { SupportedBank } from "@hooks/useSupportedBank";
import LoadingModal from "@components/LoadingModal/LoadingModal";
import EmptyList from "@components/EmptyList/EmptyList";
import { GRAYSCALE_500, WHITE } from "@styles/colors";
import Snackbar from "@components/Snackbar/Snackbar";
import { ModalWithIcon } from "@components/ModalWithIcon/ModalWithIcon";
import useBalance from "@hooks/useBalance";
import BankAlertBox from "@components/BankAlertBox/BankAlertBox";
import useRedemptionLimit from "@hooks/useRedemptionLimit";
import useFavoritePaymentBanks from "@hooks/useFavoritePaymentBanks";
import { setHeaderColor } from "@lib/telegram";

const MINIMUM_BANK_TRANSFER_PAYMENT_AMOUNT = 10000
const FOCUS_SCROLL_DELAY = 500;
const SCROLL_OFFSET_ACCOUNT_NUMBER = 100;
const SCROLL_OFFSET_AMOUNT = 200;
const SCROLL_OFFSET_MEMO = 300;
const SCROLL_OFFSET_TAG = 350;

const BankTransferPayment = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const tagInputRef = useRef<HTMLInputElement>(null);
    const accountInputRef = useRef<HTMLInputElement>(null);
    const { paymentInformation } = location.state || { paymentInformation: null };
    const { data: bankTransferPaymentFee } = useBankTransferPaymentFee()
    const {
        supportedBanks,
        bankAsRedemptionOption,
        isLoading,
        prepareBankPayment
    } = useManagePaymentBank()
    const { data: balance, isLoading: isBalanceLoading } = useBalance()
    const { data: redemptionLimit, isLoading: isRedemptionLimitLoading } = useRedemptionLimit()
    const { isFavorite, toggleFavorite } = useFavoritePaymentBanks('PAYMENT')
    const [isLocalLoading, setIsLocalLoading] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [voidModalVisible, setVoidModalVisible] = useState(false)
    const [failedModalVisible, setFailedModalVisible] = useState(false)

    // Safely parse the payment information if it exists
    const parsedPaymentInfo = React.useMemo(() => {
        if (!paymentInformation) return null;
        try {
            return JSON.parse(paymentInformation);
        } catch (error) {
            return null;
        }
    }, [paymentInformation]);

    const paymentDestination = parsedPaymentInfo?.paymentDestination || {};

    const [selectedBank, setSelectedBank] = useState<SupportedBank>(
        paymentDestination?.selectedBank || supportedBanks?.[0]
    );
    const [isOpen, setIsOpen] = useState(false);

    const { amount, handleKeyboardPress, setAmount } = useNumericKeyboard(
        parsedPaymentInfo?.amount || 0
    );

    const [accountNumber, setAccountNumber] = useState(
        paymentDestination?.accountNumber || ''
    );
    const [memo, setMemo] = useState(
        parsedPaymentInfo?.memo || ''
    );
    const [tag, setTag] = useState(
        paymentDestination?.accountTag || ''
    );

    const reservedTag = useRef(paymentDestination?.accountTag || '')

    const [amountError, setAmountError] = useState({
        isError: false,
        message: ''
    })
    const [searchQuery, setSearchQuery] = useState('');
    const [saveToFavorites, setSaveToFavorites] = useState(false);

    const validateAmount = (amountToValidate?: number) => {
        const valueToValidate = amountToValidate ?? amount
        const remainingLimit = Number(redemptionLimit.redeem_request_remaining || 0)
        const totalDailyLimit = Number(redemptionLimit.total_redeem_request_limit || 0)
        if (valueToValidate < MINIMUM_BANK_TRANSFER_PAYMENT_AMOUNT) {
            setAmountError({
                isError: true,
                message: t('bankTransferPayment.form.errorMessages.minimumAmount', { amount: toCurrencyFormat(MINIMUM_BANK_TRANSFER_PAYMENT_AMOUNT) })
            })
        } else if (typeof balance?.confirmed_balance !== 'number' || valueToValidate > balance?.confirmed_balance) {
            setAmountError({
                isError: true,
                message: t('bankTransferPayment.form.errorMessages.insufficientBalance')
            })
        } else if (valueToValidate > totalDailyLimit) {
            setAmountError({
                isError: true,
                message: t('redeem.common.errorMessages.maximumAmount', { amount: toCurrencyFormat(totalDailyLimit) })
            })
        } else if (valueToValidate > remainingLimit) {
            setAmountError({
                isError: true,
                message: t('redeem.common.errorMessages.maximumAmount', { amount: toCurrencyFormat(remainingLimit) })
            })
        } else {
            setAmountError({
                isError: false,
                message: ''
            })
        }
    }

    const handleSubmit = async () => {
        try {
            setSnackbarMessage('')
            setIsLocalLoading(true)

            // check if selected bank is already added
            const selectedBankRedemptionOption = bankAsRedemptionOption.find(bank => bank.accountNumber === accountNumber && bank.provider === selectedBank.display_name)

            // if selected bank is already added
            if (selectedBankRedemptionOption) {

                // if tag is changed
                if (reservedTag.current !== tag) {
                    const response = await prepareBankPayment({
                        bank_uuid: selectedBankRedemptionOption.id,
                        account_nickname: tag,
                        bank_name: selectedBank.code,
                        bank_account: accountNumber,
                    })

                    if (saveToFavorites !== isFavorite(response.bank_uuid)) {
                        toggleFavorite(response.bank_uuid)
                    }

                    navigate('/bank-transfer-payment-confirmation', {
                        state: {
                            paymentInformation: JSON.stringify({
                                paymentDestination: {
                                    uuid: response.bank_uuid,
                                    selectedBank: selectedBank,
                                    accountNumber,
                                    accountHolderName: response.bank_account_name,
                                    accountTag: tag,
                                },
                                amount,
                                fee: bankTransferPaymentFee.ui_fee,
                                memo,
                            })
                        }
                    })
                } else {
                    // if tag is not changed
                    if (saveToFavorites !== isFavorite(selectedBankRedemptionOption.id)) {
                        toggleFavorite(selectedBankRedemptionOption.id)
                    }

                    navigate('/bank-transfer-payment-confirmation', {
                        state: {
                            paymentInformation: JSON.stringify({
                                paymentDestination: {
                                    uuid: selectedBankRedemptionOption.id,
                                    selectedBank: selectedBank,
                                    accountNumber,
                                    accountHolderName: selectedBankRedemptionOption.name,
                                    accountTag: tag,
                                },
                                amount,
                                fee: bankTransferPaymentFee.ui_fee,
                                memo,
                            })
                        }
                    })
                }
            } else {
                // if bank is not added
                const response = await prepareBankPayment({
                    account_nickname: tag,
                    bank_name: selectedBank.code,
                    bank_account: accountNumber,
                })

                if (saveToFavorites) {
                    toggleFavorite(response.bank_uuid)
                }

                navigate('/bank-transfer-payment-confirmation', {
                    state: {
                        paymentInformation: JSON.stringify({
                            paymentDestination: {
                                uuid: response.bank_uuid,
                                selectedBank: selectedBank,
                                accountNumber,
                                accountHolderName: response.bank_account_name,
                                accountTag: tag,
                            },
                            amount,
                            fee: bankTransferPaymentFee.ui_fee,
                            memo,
                        })
                    }
                })
            }
        } catch (error) {
            if (error === 'VALIDATION_REQUESTED') {
                setVoidModalVisible(true)
            } else if (error === 'INVALID') {
                setFailedModalVisible(true)
            } else {
                setSnackbarMessage('Failed to submit bank transfer payment')
            }
        } finally {
            setIsLocalLoading(false)
        }
    }

    const handleAccountNumberFocus = () => {
        setTimeout(() => {
            if (containerRef.current) {
                const startPosition = containerRef.current.scrollTop;
                const targetPosition = SCROLL_OFFSET_ACCOUNT_NUMBER;
                const duration = 100; // milliseconds - adjust for faster/slower animation
                const startTime = performance.now();

                const animateScroll = (currentTime: number) => {
                    const elapsedTime = currentTime - startTime;
                    const progress = Math.min(elapsedTime / duration, 1);
                    // Use easeOutQuad easing function for a natural feel
                    const easeProgress = 1 - (1 - progress) * (1 - progress);

                    containerRef.current!.scrollTop = startPosition + (targetPosition - startPosition) * easeProgress;

                    if (progress < 1) {
                        requestAnimationFrame(animateScroll);
                    }
                };

                requestAnimationFrame(animateScroll);
            }
        }, FOCUS_SCROLL_DELAY);
    }

    const handleAmountFocus = () => {
        setTimeout(() => {
            if (containerRef.current) {
                const startPosition = containerRef.current.scrollTop;
                const targetPosition = SCROLL_OFFSET_AMOUNT;
                const duration = 100; // milliseconds - adjust for faster/slower animation
                const startTime = performance.now();

                const animateScroll = (currentTime: number) => {
                    const elapsedTime = currentTime - startTime;
                    const progress = Math.min(elapsedTime / duration, 1);
                    // Use easeOutQuad easing function for a natural feel
                    const easeProgress = 1 - (1 - progress) * (1 - progress);

                    containerRef.current!.scrollTop = startPosition + (targetPosition - startPosition) * easeProgress;

                    if (progress < 1) {
                        requestAnimationFrame(animateScroll);
                    }
                };

                requestAnimationFrame(animateScroll);
            }
        }, FOCUS_SCROLL_DELAY);
    }

    const handleMemoFocus = () => {
        setTimeout(() => {
            if (containerRef.current) {
                const startPosition = containerRef.current.scrollTop;
                const targetPosition = SCROLL_OFFSET_MEMO;
                const duration = 100; // milliseconds - adjust for faster/slower animation
                const startTime = performance.now();

                const animateScroll = (currentTime: number) => {
                    const elapsedTime = currentTime - startTime;
                    const progress = Math.min(elapsedTime / duration, 1);
                    // Use easeOutQuad easing function for a natural feel
                    const easeProgress = 1 - (1 - progress) * (1 - progress);

                    containerRef.current!.scrollTop = startPosition + (targetPosition - startPosition) * easeProgress;

                    if (progress < 1) {
                        requestAnimationFrame(animateScroll);
                    }
                };

                requestAnimationFrame(animateScroll);
            }
        }, FOCUS_SCROLL_DELAY);
    }

    const handleTagFocus = () => {
        setTimeout(() => {
            if (containerRef.current) {
                const startPosition = containerRef.current.scrollTop;
                const targetPosition = SCROLL_OFFSET_TAG;
                const duration = 100; // milliseconds - adjust for faster/slower animation
                const startTime = performance.now();

                const animateScroll = (currentTime: number) => {
                    const elapsedTime = currentTime - startTime;
                    const progress = Math.min(elapsedTime / duration, 1);
                    // Use easeOutQuad easing function for a natural feel
                    const easeProgress = 1 - (1 - progress) * (1 - progress);

                    containerRef.current!.scrollTop = startPosition + (targetPosition - startPosition) * easeProgress;

                    if (progress < 1) {
                        requestAnimationFrame(animateScroll);
                    }
                };

                requestAnimationFrame(animateScroll);
            }
        }, FOCUS_SCROLL_DELAY);
    }

    useEffect(() => {
        setHeaderColor(WHITE)
    }, [])

    return (
        <Container>
            <Header title={t('bankTransferPayment.header')} showBackButton onBackClick={() => navigate("/home")} />
            <LoadingModal
                visible={
                    isLoading ||
                    isLocalLoading ||
                    isBalanceLoading ||
                    isRedemptionLimitLoading}
            />
            <Snackbar
                message={snackbarMessage}
                isVisible={!!snackbarMessage}
                width="90%"
                onClose={() => setSnackbarMessage('')}
            />
            <div style={{ zIndex: 1000 }}>
                <ModalWithIcon
                    title={t('errors.verification.inProgress.bank.title')}
                    description={t('errors.verification.inProgress.bank.description')}
                    onPositive={() => setVoidModalVisible(false)}
                    positiveText={t('errors.verification.inProgress.positiveText')}
                    visible={voidModalVisible}
                    type="void"
                />
                <ModalWithIcon
                    title={t('errors.verification.failed.bank.title')}
                    description={t('errors.verification.failed.bank.description')}
                    onPositive={() => setFailedModalVisible(false)}
                    positiveText={t('errors.verification.failed.positiveText')}
                    visible={failedModalVisible}
                    type="failed"
                />
            </div>
            <Body ref={containerRef}>
                <Content>
                    <HeaderContainer>
                        <HeaderTitle>
                            {t('bankTransferPayment.destinationAccount')}
                        </HeaderTitle>
                        <HistoryLink onClick={() => setIsOpen(!isOpen)}>
                            {t('bankTransferPayment.history')}
                        </HistoryLink>
                    </HeaderContainer>
                    <FormContainer>
                        <DropdownContainer>
                            <Dropdown
                                label={t('bankTransferPayment.form.selectBank')}
                                options={supportedBanks}
                                value={selectedBank}
                                onChange={(value) => setSelectedBank(value)}
                                outline={false}
                                selectedOptionFontFamily={INTER.semiBold}
                                selectedOptionFontSize="14px"
                            />
                        </DropdownContainer>
                        <InputContainer>
                            <TextInput
                                title={t('bankTransferPayment.form.accountNumber')}
                                placeholder={t('bankTransferPayment.form.accountNumberPlaceholder')}
                                onChange={(e) => {
                                    setAccountNumber(e.target.value)
                                }}
                                onFocus={handleAccountNumberFocus}
                                value={accountNumber}
                                borderless
                                fontFamily={INTER.semiBold}
                                fontSize="14px"
                                inputMode="numeric"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === 'Tab') {
                                        e.preventDefault();
                                        accountInputRef.current?.blur();
                                    }
                                }}
                                ref={accountInputRef}
                            />
                        </InputContainer>

                        <InputContainer>
                            <RupiahInput
                                amount={amount}
                                onMax={typeof balance?.confirmed_balance === 'number' ? () => {
                                    const maxAmount = Math.min(Number(balance.confirmed_balance), Number(redemptionLimit?.redeem_request_remaining ?? 0), Number(redemptionLimit?.total_redeem_request_limit ?? 0))
                                    setAmount(maxAmount)
                                    setTimeout(() => validateAmount(maxAmount), 0)
                                } : undefined}
                                isAmountError={amountError.isError}
                                errorMessage={amountError.message}
                                handleKeyboardPress={handleKeyboardPress}
                                onBlur={() => validateAmount()}
                                onFocus={handleAmountFocus}
                            />
                        </InputContainer>

                        <InputContainer>
                            <TextInput
                                title={t('bankTransferPayment.form.memo')}
                                isOptional
                                placeholder={t('bankTransferPayment.form.memoPlaceholder')}
                                onChange={(e) => {
                                    setMemo(e.target.value)
                                }}
                                onFocus={handleMemoFocus}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === 'Tab') {
                                        e.preventDefault();
                                        tagInputRef.current?.focus();
                                    }
                                }}
                                value={memo}
                                borderless
                                fontFamily={INTER.semiBold}
                                fontSize="14px"
                            />
                        </InputContainer>

                        <InputContainer>
                            <TextInput
                                title={t('bankTransferPayment.form.tag')}
                                isOptional
                                placeholder={t('bankTransferPayment.form.tagPlaceholder')}
                                onChange={(e) => {
                                    setTag(e.target.value)
                                }}
                                onFocus={handleTagFocus}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === 'Tab') {
                                        e.preventDefault();
                                        tagInputRef.current?.blur();
                                    }
                                }}
                                value={tag}
                                borderless
                                fontFamily={INTER.semiBold}
                                fontSize="14px"
                                ref={tagInputRef}
                            />
                        </InputContainer>

                        <SaveToFavoritesContainer>
                            <Checkbox
                                checked={saveToFavorites}
                                onChange={() => {
                                    setSaveToFavorites(prev => !prev)
                                }}
                                label={t('bankTransferPayment.form.saveToFavorites')}
                            />
                        </SaveToFavoritesContainer>
                        <HintContainer>
                            <HintInner>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, marginTop: 1 }}>
                                    <circle cx="8" cy="8" r="7" stroke="#5C5C5C" strokeWidth="1.2"/>
                                    <path d="M8 7V11" stroke="#5C5C5C" strokeWidth="1.2" strokeLinecap="round"/>
                                    <circle cx="8" cy="5" r="0.75" fill="#5C5C5C"/>
                                </svg>
                                <HintText>{t('bankTransferPayment.form.favoriteHint')}</HintText>
                            </HintInner>
                        </HintContainer>
                    </FormContainer>
                </Content>
                <BankAlertBox
                    inputtedAmount={amount}
                    splitDisbursementCountLimit={redemptionLimit?.maximum_split_disbursements}
                    cutOffTime={selectedBank?.cut_off_time}
                    payment
                    maximumAmountPerDisbursement={selectedBank?.max_amount}
                />
            </Body>

            <ButtonContainer>
                <PrimaryButton
                    disabled={!selectedBank || !amount || !accountNumber || amountError.isError}
                    onClick={handleSubmit}
                >
                    {t('bankTransferPayment.form.continueButton')}
                </PrimaryButton>
            </ButtonContainer>

            <BottomSheet
                isOpen={isOpen}
                header={<BottomSheetHeader>
                    <SearchBar
                        value={searchQuery}
                        onChange={(value) => setSearchQuery(value)}
                    />
                </BottomSheetHeader>}
                onClose={() => setIsOpen(!isOpen)}
            >
                {(() => {
                    const filtered = filterBySearchQuery(bankAsRedemptionOption, searchQuery, ['name', 'provider', 'nickname', 'accountNumber'])
                    if (filtered.length === 0) {
                        return (
                            <EmptyStateContainer>
                                <EmptyList backgroundColor={GRAYSCALE_500} />
                                <span>{t('bankTransferPayment.emptyPaymentList')}</span>
                            </EmptyStateContainer>
                        )
                    }
                    const sorted = [...filtered].sort((a, b) => {
                        const aFav = isFavorite(a.id) ? 0 : 1
                        const bFav = isFavorite(b.id) ? 0 : 1
                        return aFav - bFav
                    })
                    return sorted.map((account, index) => (
                        <React.Fragment key={account.id}>
                            <AccountListItem
                                name={account.name}
                                iconUrl={account.iconUrl}
                                accountNumber={account.accountNumber}
                                provider={account.provider}
                                isValid={account.isValid}
                                nickname={account.nickname}
                                isFavorite={isFavorite(account.id)}
                                onFavoriteToggle={(e) => {
                                    e.stopPropagation()
                                    toggleFavorite(account.id)
                                }}
                                onClick={() => {
                                    reservedTag.current = account.nickname
                                    setSelectedBank(supportedBanks.find(bank => String(bank.display_name) === String(account.provider)) as SupportedBank)
                                    setAccountNumber(account.accountNumber)
                                    setMemo('')
                                    setTag(account.nickname)
                                    setSaveToFavorites(isFavorite(account.id))
                                    setIsOpen(!isOpen)
                                }}
                            />
                            {index !== sorted.length - 1 && <Divider />}
                        </React.Fragment>
                    ))
                })()}
            </BottomSheet>
        </Container>
    )
}

export default BankTransferPayment

